import * as THREE from 'three';

const AVATAR_COLORS = [0xf5ba49, 0x48baa7, 0xe56c8a, 0x6f8ce8];

/**
 * Simple crew avatar shown on the shared deck. Position is local to the ship,
 * so avatars inherit the ship's interpolated pose automatically.
 */
export class PlayerAvatar {
  readonly group = new THREE.Group();
  readonly color: number;

  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];

  constructor(index: number, readonly isLocal = false) {
    this.color = AVATAR_COLORS[index % AVATAR_COLORS.length];
    this.group.name = isLocal ? 'avatar:local' : 'avatar:remote';

    const bodyMaterial = this.material(
      new THREE.MeshStandardMaterial({
        color: this.color,
        emissive: this.color,
        emissiveIntensity: isLocal ? 0.35 : 0.15,
        roughness: 0.5,
        metalness: 0.08,
      }),
    );

    const body = new THREE.Mesh(this.geometry(new THREE.CapsuleGeometry(0.28, 0.5, 6, 12)), bodyMaterial);
    body.position.y = 0.85;
    body.castShadow = true;
    this.group.add(body);

    const head = new THREE.Mesh(this.geometry(new THREE.SphereGeometry(0.22, 12, 10)), bodyMaterial);
    head.position.y = 1.42;
    head.castShadow = true;
    this.group.add(head);

    if (isLocal) {
      const halo = new THREE.Mesh(
        this.geometry(new THREE.TorusGeometry(0.42, 0.035, 8, 20)),
        this.material(new THREE.MeshBasicMaterial({ color: 0xffffff })),
      );
      halo.rotation.x = -Math.PI / 2;
      halo.position.y = 0.06;
      this.group.add(halo);
    }
  }

  /** Place the avatar in ship-local deck space. */
  setLocalPosition(x: number, y: number, z: number): void {
    this.group.position.set(x, y, z);
  }

  /**
   * Set the avatar's deck-relative facing (radians, 0 = bow +X, positive toward
   * port). Cosmetic today — the capsule is symmetric — but it lands the state
   * and API that the walk animation and modelled avatars will consume later.
   */
  setFacing(yaw: number): void {
    this.group.rotation.y = yaw;
  }

  private geometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.geometries.push(geometry);
    return geometry;
  }

  private material<T extends THREE.Material>(material: T): T {
    this.materials.push(material);
    return material;
  }

  dispose(): void {
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
  }
}
